import React, { useEffect, useState, useRef } from 'react';
import { ProductPage as ProductPageType } from '@/lib/TicketApi';
import ProductSection from './ProductSection';
import useQueue from '@/hooks/useQueue';
import { useShop } from '@/providers/ShopProvider';
import { QueueContextProvider } from '@/providers/QueueContext';
import { QueueOverlay } from '@/components/queue/QueueOverlay';
import { ShopUnlockCard } from '@/components/shared/ShopUnlockCard';
import { eventEmitter } from '@/lib/eventEmitter';
import { shopVar } from '@/lib/shopStyles';
import clsx from 'clsx';
import { playNotificationSound } from '@/assets/notification';

interface ProductPageProps {
  productPage: ProductPageType;
}

export const ProductPage: React.FC<ProductPageProps> = ({ productPage }) => {
  const { shop } = useShop();
  const { queueStatus, isError: isQueueError, saleStartsAt } = useQueue(shop.queueId, shop.queueEndpoint);
  const { isWidget } = useShop();
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const previousStatusRef = useRef<string>();

  const hasQueue = !!(shop.queueId && shop.queueEndpoint);
  const isActive = queueStatus?.status === 'active';
  const isQueueBlocking = hasQueue && (!queueStatus || !isActive);

  // Update queue context when status changes
  useEffect(() => {
    // Queue is blocking the shop when: queue exists AND status is not 'active'
    QueueContextProvider.updateQueueState(isQueueBlocking && queueStatus?.status !== 'converted');

    // When queue becomes active, refresh shop data to get latest product availability
    if (isActive) {
      eventEmitter.emit('RELOAD_SHOP_CONFIG', {
        shopSlug: shop.slug,
      });

      // Only notify if status just changed to active
      if (notificationsEnabled && previousStatusRef.current && previousStatusRef.current !== 'active') {
        playNotificationSound();

        if (Notification.permission === 'granted') {
          const eventName = shop.events[0]?.name || 'the event';
          const notification = new Notification('Your turn!', {
            body: `You can now access the shop and buy tickets for ${eventName}.`,
            icon: 'https://cdn.prod.website-files.com/66bc85afc94c2274121979c7/66bc976f0d0cd70b55ff38fa_fafvicon.png',
          });

          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }
      }
    }

    previousStatusRef.current = queueStatus?.status;
  }, [queueStatus?.status, shop.slug, notificationsEnabled, isActive, isQueueBlocking]);

  // Error state: show countdown if we know the sale start time and it's in the future
  const isBeforeSale = saleStartsAt && new Date(saleStartsAt).getTime() > Date.now();
  if (hasQueue && isQueueError && isBeforeSale) {
    const fallbackStatus: import('@/hooks/useQueue').QueueResponse = {
      status: 'not_open',
      opensAt: saleStartsAt,
      saleStartsAt: saleStartsAt,
      pollAfterSeconds: 0,
    };
    return (
      <div
        className={clsx('py-2 px-8 rounded-md justify-between items-center', isWidget ? 'mt-4' : '')}
        style={{ backgroundColor: shopVar('--shop-panelBackgroundColor') }}>
        <QueueOverlay
          queueStatus={fallbackStatus}
          onNotificationsEnabled={() => setNotificationsEnabled(true)}
          notificationsEnabled={notificationsEnabled}
        />
      </div>
    );
  }

  // Loading state while queue status is being fetched
  if (hasQueue && queueStatus === null) {
    return (
      <div
        className={clsx('py-2 px-8 rounded-md justify-between items-center', isWidget ? 'mt-4' : '')}
        style={{ backgroundColor: shopVar('--shop-panelBackgroundColor') }}>
        <div className="flex animate-pulse space-x-4">
          <div className="size-10 rounded-full bg-gray-200"></div>
          <div className="flex-1 space-y-6 py-1">
            <div className="h-2 rounded bg-gray-200"></div>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 h-2 rounded bg-gray-200"></div>
                <div className="col-span-1 h-2 rounded bg-gray-200"></div>
              </div>
              <div className="h-2 rounded bg-gray-200"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show queue overlay for not_open, queued, or converted statuses
  if (hasQueue && queueStatus && queueStatus.status !== 'active') {
    return (
      <div
        className={clsx('py-2 px-8 rounded-md justify-between items-center', isWidget ? 'mt-4' : '')}
        style={{ backgroundColor: shopVar('--shop-panelBackgroundColor') }}>
        <QueueOverlay
          queueStatus={queueStatus}
          onNotificationsEnabled={() => setNotificationsEnabled(true)}
          notificationsEnabled={notificationsEnabled}
        />
      </div>
    );
  }

  // Show normal product sections when no queue or status is active
  return (
    <>
      <ShopUnlockCard />
      {productPage.productSections.map((productSection) => (
        <ProductSection key={productSection.id} productSection={productSection} />
      ))}
    </>
  );
};
